import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import type { Database } from "@/integrations/supabase/types";

interface UserRole {
  id: string;
  user_id: string;
  role: Database["public"]["Enums"]["app_role"];
  created_at: string;
  user_email: string;
}

export function UserRolesManagement() {
  const { toast } = useToast();
  const { isAdmin, error: adminError, isLoading: authLoading } = useAdminAuth();
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<UserRole | null>(null);
  const [formData, setFormData] = useState({
    user_id: "",
    role: "user" as Database["public"]["Enums"]["app_role"],
  });

  const fetchData = async () => {
    try {
      console.log('Fetching user roles...');
      
      // First get the current session to ensure we're authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('No session found');
        return;
      }

      // Fetch user roles data
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('id, user_id, role, created_at')
        .order('created_at', { ascending: false });

      if (rolesError) {
        console.error('Error fetching roles:', rolesError);
        throw rolesError;
      }

      // For each role, fetch the corresponding user email
      const rolesWithEmails = await Promise.all(
        (rolesData || []).map(async (role) => {
          const { data: userData, error: userError } = await supabase
            .from('user_roles')
            .select('user_id')
            .eq('user_id', role.user_id)
            .single();

          if (userError) {
            console.error('Error fetching user data:', userError);
            return {
              ...role,
              user_email: 'Unknown Email'
            };
          }

          return {
            id: role.id,
            user_id: role.user_id || '',
            role: role.role || 'user',
            created_at: role.created_at || new Date().toISOString(),
            user_email: userData?.user_id || 'Unknown Email'
          };
        })
      );

      console.log('Processed user roles:', rolesWithEmails);
      setUserRoles(rolesWithEmails);

    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch user roles",
      });
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const handleDelete = async (id: string) => {
    try {
      if (!id) {
        throw new Error('Invalid role ID');
      }
      
      console.log('Deleting user role:', id);
      const { error } = await supabase.from("user_roles").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Success", description: "User role deleted successfully" });
      fetchData();
    } catch (error) {
      console.error("Error deleting user role:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete user role",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!formData.user_id) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Please select a user",
        });
        return;
      }

      if (editingRole) {
        console.log('Updating user role:', editingRole.id, formData);
        const { error } = await supabase
          .from("user_roles")
          .update(formData)
          .eq("id", editingRole.id);
        if (error) throw error;
        toast({ title: "Success", description: "User role updated successfully" });
      } else {
        console.log('Creating new user role:', formData);
        const { error } = await supabase.from("user_roles").insert([formData]);
        if (error) throw error;
        toast({ title: "Success", description: "User role added successfully" });
      }
      setFormData({ user_id: "", role: "user" });
      setIsAddOpen(false);
      setIsEditOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error saving user role:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save user role",
      });
    }
  };

  const handleEdit = (role: UserRole) => {
    setEditingRole(role);
    setFormData({
      user_id: role.user_id,
      role: role.role,
    });
    setIsEditOpen(true);
  };

  if (authLoading) {
    return <div>Loading...</div>;
  }

  if (adminError) {
    return <div className="text-red-500">{adminError}</div>;
  }

  if (!isAdmin) {
    return <div>You must be an admin to manage user roles</div>;
  }

  const UserRoleForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Select
        value={formData.user_id}
        onValueChange={(value) => setFormData({ ...formData, user_id: value })}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select User" />
        </SelectTrigger>
        <SelectContent>
          {userRoles.map((role) => (
            <SelectItem key={role.user_id} value={role.user_id}>
              {role.user_email}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={formData.role}
        onValueChange={(value) => setFormData({ ...formData, role: value as Database["public"]["Enums"]["app_role"] })}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select Role" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="user">User</SelectItem>
          <SelectItem value="admin">Admin</SelectItem>
        </SelectContent>
      </Select>

      <Button type="submit">{editingRole ? "Update" : "Add"} Role</Button>
    </form>
  );

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">User Roles Management</h2>
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-2">
              <Plus className="h-4 w-4" /> Add User Role
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New User Role</DialogTitle>
            </DialogHeader>
            <UserRoleForm />
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User Role</DialogTitle>
          </DialogHeader>
          <UserRoleForm />
        </DialogContent>
      </Dialog>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Created At</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {userRoles.map((role) => (
            <TableRow key={role.id}>
              <TableCell>{role.user_email}</TableCell>
              <TableCell className="capitalize">{role.role}</TableCell>
              <TableCell>{new Date(role.created_at).toLocaleDateString()}</TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(role)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(role.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}